
import ServiceTemplate from '../../ServiceTemplate';
import SEOHead from '../../../../components/SEOHead';

export default function KitchenUnderCabinetLightingPage() {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://electriciansolutionsfresno.com';

  const schema = {
    '@context': 'https://schema.org',
    '@type': 'Service',
    serviceType: 'Kitchen Under-Cabinet Lighting Installation',
    provider: {
      '@type': 'ElectricalContractor',
      name: 'Fresno Electrical Services',
      address: {
        '@type': 'PostalAddress',
        addressLocality: 'Fresno',
        addressRegion: 'CA',
        addressCountry: 'US',
      },
    },
    areaServed: {
      '@type': 'City',
      name: 'Fresno',
      '@id': 'https://en.wikipedia.org/wiki/Fresno,_California',
    },
    description:
      'Professional under-cabinet lighting installation in Fresno, CA. LED strip lights, puck lights, and task lighting for kitchen countertops.',
    url: `${siteUrl}/services/lighting-outdoor/kitchen-under-cabinet-lighting`,
  };

  const features = [
    {
      icon: 'ri-lightbulb-flash-line',
      title: 'LED Strip Lighting',
      description: 'Continuous, even illumination along entire countertop length',
    },
    {
      icon: 'ri-flashlight-fill',
      title: 'Puck Light Installation',
      description: 'Focused task lighting for specific work areas',
    },
    {
      icon: 'ri-contrast-2-line',
      title: 'Dimmable Controls',
      description: 'Adjust brightness for cooking, dining, or ambient lighting',
    },
    {
      icon: 'ri-plug-line',
      title: 'Hardwired Installation',
      description: 'Clean, permanent installation with no visible cords or plugs',
    },
    {
      icon: 'ri-smartphone-line',
      title: 'Smart Lighting Options',
      description: 'Control under-cabinet lights with apps or voice commands',
    },
    {
      icon: 'ri-palette-line',
      title: 'Color Temperature',
      description: 'Choose warm white, neutral, or daylight LED options',
    },
  ];

  return (
    <>
      <SEOHead
        title="Under Cabinet Lighting Fresno CA | Kitchen Task Lighting"
        description="Professional under-cabinet lighting installation in Fresno, CA. LED strip lights, puck lights, and dimmable task lighting for kitchen countertops. Licensed electricians."
        canonical="/services/lighting-outdoor/kitchen-under-cabinet-lighting"
        keywords="under cabinet lighting Fresno, kitchen task lighting, LED strip lights, puck lights"
        schema={schema}
      />
      <ServiceTemplate
        heroTitle="Kitchen Under-Cabinet Lighting — Fresno, CA"
        heroSubtitle="Illuminate your Fresno kitchen countertops with professional under-cabinet lighting"
        heroImage="https://public.readdy.ai/ai/img_res/07bd28ae00ff6a9f762bc2b61fe283f4.jpg"
        fresnoImage="https://readdy.ai/api/search-image?query=Close%20up%20of%20LED%20strip%20lights%20installed%20under%20white%20kitchen%20cabinets%20illuminating%20marble%20countertop%20and%20tile%20backsplash%20with%20warm%20white%20glow%20California%20home%20no%20people%20present&width=700&height=600&seq=undercab-light-fresno-004&orientation=portrait"
        fresnoContent="Transform your Fresno kitchen with professional under-cabinet lighting that eliminates shadows and enhances functionality. From Tower District renovations to new Fig Garden builds, we install energy-efficient LED strip lights and puck lights that provide perfect task lighting for food prep, cooking, and entertaining. Our licensed electricians hardwire fixtures for a clean, cord-free look and install dimmer controls so you can adjust brightness from bright task lighting to soft ambient glow."
        features={features}
        faqs={[
          {
            question:
              'What type of under-cabinet lighting is best for Fresno kitchens?',
            answer:
              'For Fresno kitchens, we recommend LED strip lights or LED puck lights depending on your needs. LED strip lights provide continuous, even illumination along the entire countertop with no dark spots—ideal for food prep and cooking. LED puck lights offer focused task lighting in specific areas and work well for shorter cabinet runs. Both options are energy-efficient, produce minimal heat (important in Fresno summers), and last 25+ years. We assess your kitchen layout and recommend the best solution during consultation.',
          },
          {
            question: 'How much does under-cabinet lighting installation cost in Fresno?',
            answer:
              'Under-cabinet lighting installation in Fresno typically costs $400–$1,200 depending on kitchen size and fixture type. LED strip lighting for a standard 10-foot run costs $500–$800 including hardwired installation and dimmer switch. Puck light installations range from $400–$1,000 for 6–10 fixtures. Prices include all materials, labor, electrical work, and cleanup. We provide free consultations and detailed quotes for all Fresno kitchen projects. Most homeowners see immediate ROI through increased home value and improved functionality.',
          },
          {
            question: 'Should under-cabinet lights be hardwired or plug-in in Fresno?',
            answer:
              "We strongly recommend hardwired under-cabinet lighting for Fresno homes. Hardwired installations provide a clean, professional look with no visible cords or plugs cluttering your countertops. They're more reliable, can be controlled by wall switches or dimmers, and integrate seamlessly with your kitchen design. Plug-in lights are cheaper initially but look less polished and limit outlet availability. Our licensed electricians hardwire fixtures directly to your electrical system, ensuring code compliance and long-term reliability.",
          },
          {
            question: 'Can you install dimmable under-cabinet lighting in Fresno?',
            answer:
              'Absolutely! We install dimmable under-cabinet lighting throughout Fresno. Dimmer controls allow you to adjust brightness from 100% for cooking and food prep to 10% for ambient evening lighting or entertaining. We install LED‑compatible dimmer switches that provide smooth, flicker‑free dimming. Many Fresno homeowners also choose smart dimmer systems that integrate with Alexa, Google Home, or smartphone apps for voice and remote control. Dimmable lighting adds versatility and saves energy.',
          },
          {
            question: 'How long does under-cabinet lighting installation take in Fresno?',
            answer:
              'Most under-cabinet lighting installations in Fresno are completed in 4–8 hours (1 day). A standard kitchen with 10–15 feet of LED strip lighting typically takes 4–6 hours including hardwiring, dimmer installation, and testing. Larger kitchens or complex installations with multiple zones may require a full day. We work efficiently to minimize disruption and always clean up thoroughly. Many Fresno homeowners schedule installations during kitchen renovations for seamless integration.',
          },
          {
            question: 'Do I need permits for under-cabinet lighting in Fresno?',
            answer:
              "Hardwired under-cabinet lighting installations in Fresno typically require electrical permits since they involve modifying your home's electrical system. Plug-in fixtures don't require permits but aren't recommended for permanent installations. We handle all permit applications and inspections with the City of Fresno, ensuring full compliance with California electrical codes. Our licensed C‑10 electricians ensure all work meets Fresno building standards. Proper permitting protects your home value and ensures insurance coverage.",
          },
        ]}
      />
    </>
  );
}
